import React from 'react';
import { motion } from 'framer-motion';
import { Linkedin, Twitter } from 'lucide-react';
import { useContent } from '../../lib/content';
import { pickLocale } from '../../lib/api';

export default function Team() {
  const { content, locale } = useContent();
  const data = content.team || {};
  const members = data.members || [];

  return (
    <section
      id="team"
      data-testid="team-section"
      className="section bg-bg"
    >
      <div className="container-x">
        <div className="mb-14 md:mb-20 max-w-3xl">
          <p className="text-eyebrow mb-4">{pickLocale(data.eyebrow, locale)}</p>
          <h2 className="font-display text-4xl md:text-6xl lg:text-7xl tracking-tight leading-[0.95] mb-5" data-testid="team-title">
            {pickLocale(data.title, locale)}
          </h2>
          <p className="text-ink-muted text-base md:text-lg max-w-xl">
            {pickLocale(data.subtitle, locale)}
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4 md:gap-6">
          {members.map((m, i) => (
            <motion.div
              key={i}
              data-testid={`team-member-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="group relative aspect-[4/5] overflow-hidden bg-bg-surface border border-line"
            >
              {m.photo ? (
                <img loading="lazy" decoding="async"
                  src={m.photo}
                  alt={m.name}
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-500"/>
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-bg-surface to-bg">
                  <span className="font-display text-6xl text-ink-dim">
                    {(m.name || '').split(' ').map(w => w[0]).slice(0, 2).join('')}
                  </span>
                </div>
              )}
              <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-bg via-bg/80 to-transparent">
                <div className="font-display text-2xl text-ink mb-1">{m.name}</div>
                <div className="text-eyebrow text-ink-muted">{pickLocale(m.role, locale)}</div>
                {(m.social?.linkedin || m.social?.x) && (
                  <div className="flex gap-3 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
                    {m.social?.linkedin && (
                      <a href={m.social.linkedin} target="_blank" rel="noreferrer" className="text-ink-muted hover:text-acid">
                        <Linkedin size={16} />
                      </a>
                    )}
                    {m.social?.x && (
                      <a href={m.social.x} target="_blank" rel="noreferrer" className="text-ink-muted hover:text-acid">
                        <Twitter size={16} />
                      </a>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
